/**
 * Session bootstrap.
 *
 * Two paths supported (in order of preference):
 *
 *   1. Marketplace App (Custom Page) — preferred for scale.
 *      Frontend asks parent GHL window for encrypted user context via postMessage,
 *      backend decrypts with shared secret, returns JWT.
 *      No URL placeholders involved.
 *
 *   2. Legacy Custom Menu Link with URL params + per-location shared secret.
 *      Fallback for environments where the Marketplace App is not yet installed.
 *      Subject to GHL placeholder-expansion quirks.
 */

export interface IframeParams {
  locationId: string;
  userEmail: string;
  userRole: string;
  userName?: string;
  userFirstName?: string;
  userLastName?: string;
  userCompany?: string;
  userType?: string;
  v: string;
}

export interface SessionState {
  token: string;
  expiresAt: number;
  user: { id: string; email: string; name?: string; role: string; operator?: boolean };
  client: { id: string; slug: string; name: string };
  /** GHL location_id, returned by the session exchange. Authoritative source for
   *  building "open in GHL" deep links (survives in-app hash navigation, unlike
   *  reading it from the URL). May be null on agency-scoped marketplace contexts. */
  locationId: string | null;
  via: 'marketplace' | 'legacy_cml' | 'operador';
}

/**
 * No hay ninguna via de sesion en esta pagina: ni iframe de GHL ni sesion de
 * operador guardada. NO es un fallo: es la senal para pedir la contrasena de
 * operador (la app la distingue de un error de verdad por el tipo).
 */
export class SinSesionGhl extends Error {
  constructor() { super('sin_sesion_ghl'); this.name = 'SinSesionGhl'; }
}

/**
 * GHL location_id, read from BOTH the query string and the hash. The embed URL
 * often carries it after the # (e.g. /#/feedback?location_id=...), so reading
 * window.location.search alone misses it. Used to build "open in GHL" deep links.
 */
function readLocationIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const searchParams = new URLSearchParams(window.location.search);
  const hashRaw = window.location.hash ?? '';
  const hashQueryIdx = hashRaw.indexOf('?');
  const hashParams = hashQueryIdx >= 0 ? new URLSearchParams(hashRaw.slice(hashQueryIdx + 1)) : new URLSearchParams();
  return searchParams.get('location_id') ?? hashParams.get('location_id');
}

// Snapshot the location_id from the INITIAL embed URL, captured once at module
// load — before any in-app navigation. navigate() rewrites window.location.hash
// to e.g. #/calls/<id>, which drops a location_id that lived in the hash, so a
// live re-read while viewing a drill-down would return null. The session's
// locationId (from the server) is the primary source; this is the URL fallback.
const INITIAL_URL_LOCATION_ID: string | null = readLocationIdFromUrl();

export function getLocationId(): string | null {
  return INITIAL_URL_LOCATION_ID ?? readLocationIdFromUrl();
}

function readIframeParams(): IframeParams | null {
  // Params can live in window.location.search (standard) OR inside the hash
  // (e.g. URL like /#/feedback?location_id=...). The CML URLs for fullscreen
  // routes put them after the # so the hash router can pick the right view;
  // we look in both places.
  const searchParams = new URLSearchParams(window.location.search);
  const hashRaw = window.location.hash ?? '';
  const hashQueryIdx = hashRaw.indexOf('?');
  const hashParams = hashQueryIdx >= 0 ? new URLSearchParams(hashRaw.slice(hashQueryIdx + 1)) : new URLSearchParams();
  const pick = (k: string): string | null => searchParams.get(k) ?? hashParams.get(k);

  const locationId = pick('location_id');
  const userEmail = pick('user_email');
  const v = pick('v');
  if (!locationId || !userEmail || !v) return null;
  return {
    locationId,
    userEmail,
    userRole: pick('user_role') ?? 'user',
    userName: pick('user_name') ?? undefined,
    v,
  };
}

function paramsLookExpanded(params: IframeParams): boolean {
  return !params.locationId.includes('{{') && !params.userEmail.includes('{{') && !params.v.includes('{{');
}

async function requestMarketplaceUserContext(timeoutMs = 5000): Promise<string | null> {
  if (window.parent === window) return null; // not in iframe
  return new Promise<string | null>((resolve) => {
    const timer = setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve(null);
    }, timeoutMs);
    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (data && typeof data === 'object' && data.message === 'REQUEST_USER_DATA_RESPONSE') {
        clearTimeout(timer);
        window.removeEventListener('message', handler);
        resolve(typeof data.payload === 'string' ? data.payload : null);
      }
    };
    window.addEventListener('message', handler);
    window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*');
  });
}

async function exchangeMarketplace(encryptedUserData: string): Promise<SessionState> {
  const res = await fetch('/api/session/marketplace-exchange', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ encryptedUserData }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(`marketplace_exchange_${res.status}: ${JSON.stringify(json)}`);
  return {
    token: json.token,
    expiresAt: Date.now() + (json.expiresIn ?? 300) * 1000,
    user: json.user,
    client: json.client,
    locationId: json.locationId ?? null,
    via: 'marketplace',
  };
}

/**
 * ENTRADA PROPIA (sin GoHighLevel). La sesion se guarda en `sessionStorage`, no
 * en `localStorage`: sobrevive a recargar la pestana, que es lo que molesta, y
 * se va al cerrarla. Es un token que puede crear subcuentas, asi que no se deja
 * escrito mas tiempo del necesario.
 */
const CLAVE_OPERADOR = 'fluxo_sesion_operador';
/** Marca de "ya he recargado una vez por un 401", para no entrar en bucle. */
const RECARGA_HECHA = 'fluxo_operador_recarga';

function guardarOperador(s: SessionState): void {
  try { sessionStorage.setItem(CLAVE_OPERADOR, JSON.stringify(s)); } catch { /* modo privado: se queda en memoria */ }
}

function leerOperador(): SessionState | null {
  // Dentro del iframe de GHL no se usa, aunque este guardada: el iframe comparte
  // el sessionStorage de la pestana, y ahi la sesion buena es la de la subcuenta
  // que GHL acredita, no la de Fluxo.
  if (typeof window === 'undefined' || window.parent !== window) return null;
  try {
    const raw = sessionStorage.getItem(CLAVE_OPERADOR);
    if (!raw) return null;
    const s = JSON.parse(raw) as SessionState;
    // 60 s de margen: un token que caduca mientras se carga la pagina no sirve.
    if (typeof s?.expiresAt !== 'number' || s.expiresAt < Date.now() + 60_000) {
      sessionStorage.removeItem(CLAVE_OPERADOR);
      return null;
    }
    return s;
  } catch { return null; }
}

export async function entrarComoOperador(password: string): Promise<SessionState> {
  const res = await fetch('/api/session/operador', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 404) throw new Error('La entrada de operador no está activada en este servidor.');
    if (res.status === 401) throw new Error('Contraseña incorrecta.');
    if (res.status === 429) throw new Error(String(json.detail ?? 'Demasiados intentos. Espera un momento.'));
    throw new Error(String(json.detail ?? json.error ?? `error_${res.status}`));
  }
  const s: SessionState = {
    token: json.token,
    expiresAt: Date.now() + (json.expiresIn ?? 3600) * 1000,
    user: json.user,
    client: json.client,
    locationId: json.locationId ?? null,
    via: 'operador',
  };
  guardarOperador(s);
  try { sessionStorage.removeItem(RECARGA_HECHA); } catch { /* da igual */ }
  return s;
}

/**
 * Copias locales del diagnóstico (DiagnosticoComercial): datos personales del
 * cliente y notas internas que solo existen mientras el servidor no confirma.
 */
const PREFIJO_COPIAS_DIAGNOSTICO = 'fluxo:diagnostico:';

function olvidarSesionOperador(): void {
  try { sessionStorage.removeItem(CLAVE_OPERADOR); } catch { /* nada que borrar */ }
}

/** Salir: la sesión y las copias del diagnóstico no se quedan en este navegador. */
export function salirDeOperador(): void {
  olvidarSesionOperador();
  try {
    const claves: string[] = [];
    for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i); if (k?.startsWith(PREFIJO_COPIAS_DIAGNOSTICO)) claves.push(k); }
    for (const k of claves) localStorage.removeItem(k);
  } catch { /* sin almacenamiento local: no hay copias */ }
}

async function exchangeLegacy(params: IframeParams): Promise<SessionState> {
  const res = await fetch('/api/session/exchange', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      location_id: params.locationId,
      user_email: params.userEmail,
      user_role: params.userRole,
      user_name: params.userName,
      v: params.v,
      referer: document.referrer,
    }),
  });
  const json = await res.json();
  // Acceso antiguo retirado (3-oct): se ensena el mensaje para el usuario, no el error tecnico.
  if (res.status === 410 && typeof json?.detail === 'string') throw new Error(json.detail);
  if (!res.ok) throw new Error(`legacy_exchange_${res.status}: ${JSON.stringify(json)}`);
  return {
    token: json.token,
    expiresAt: Date.now() + (json.expiresIn ?? 300) * 1000,
    user: json.user,
    client: json.client,
    locationId: json.locationId ?? null,
    via: 'legacy_cml',
  };
}

/**
 * Bootstrap session. Tries Marketplace first (if running inside an iframe),
 * falls back to legacy CML params if Marketplace path returns null or fails
 * because the app is not yet installed on this location.
 */
export async function bootstrapSession(): Promise<SessionState> {
  // 0. MODO DEMO, solo en desarrollo. Existe para poder MIRAR el panel sin GHL:
  //    hoy la unica via de entrada es el iframe de una Custom Page, asi que ni
  //    desde el movil ni en local se puede abrir para verlo.
  //
  //    `import.meta.env.DEV` es false en el bundle de produccion, asi que este
  //    bloque no llega a existir en lo que se despliega (Vite lo elimina al
  //    construir: comprobado con `grep demo_token dist/assets/*.js`). El token lo
  //    firma el servidor de demo (`scripts/demo-alta.ts`), que corre con una base
  //    de datos en memoria y un GHL de mentira, nunca contra produccion.
  if (import.meta.env.DEV) {
    const demo = new URLSearchParams(window.location.search).get('demo_token');
    if (demo) {
      const res = await fetch('/api/session/demo-context', { headers: { authorization: `Bearer ${demo}` } });
      if (!res.ok) throw new Error(`demo_context_${res.status}`);
      const ctx = (await res.json()) as Omit<SessionState, 'token' | 'via'>;
      return { ...ctx, token: demo, via: 'legacy_cml' };
    }
  }

  // 0.5 Sesion de operador ya abierta en esta pestana (entrada propia).
  const yaDentro = leerOperador();
  if (yaDentro) return yaDentro;

  // 1. Try Marketplace path. If postMessage delivers a payload, that means we're
  //    inside a Custom Page iframe — server's verdict is authoritative.
  //    DO NOT fall back to legacy on server errors (would mask the real cause).
  const encrypted = await requestMarketplaceUserContext();
  if (encrypted) {
    return await exchangeMarketplace(encrypted); // throws server error verbatim
  }

  // 2. No postMessage answer → not in a Marketplace Custom Page iframe.
  //    Try legacy CML fallback (URL params + per-location secret).
  const params = readIframeParams();
  if (!params) {
    // Ninguna via de GHL. No es un error: la app pedira la contrasena de operador.
    throw new SinSesionGhl();
  }
  if (!paramsLookExpanded(params)) {
    throw new Error('GHL did not expand the placeholders in the URL. The Custom Menu Link must be scoped to a Sub-Account sidebar with the Custom Value present.');
  }
  return exchangeLegacy(params);
}

// Module-level holder so authedFetch can refresh the session on 401 without
// every caller having to pass a refresher. App.tsx calls registerSession on
// bootstrap and re-registers after each successful refresh.
let currentSession: SessionState | null = null;
let onSessionRefresh: ((s: SessionState) => void) | null = null;

export function registerSession(s: SessionState, onRefresh: (s: SessionState) => void): void {
  currentSession = s;
  onSessionRefresh = onRefresh;
}

async function doFetch(token: string, path: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return fetch(path, { ...init, headers });
}

export async function authedFetch(session: SessionState, path: string, init: RequestInit = {}): Promise<Response> {
  const useSession = currentSession ?? session;
  let res = await doFetch(useSession.token, path, init);
  if (res.status === 401) {
    // Sesion de operador caducada (dura horas, pero caduca). Aqui no hay a quien
    // pedirle una nueva: GHL no esta. Se tira la guardada y se recarga, que deja
    // la pantalla de entrar. Sin esto se quedaba pidiendo con el mismo token
    // muerto y el panel se veia en blanco.
    if (useSession.via === 'operador') {
      // Caducar no es salir: la copia local del diagnóstico se conserva porque,
      // tras volver a entrar (el hash sobrevive a la recarga), es lo único que
      // queda del guardado que acaba de fallar con este 401.
      olvidarSesionOperador();
      // UNA sola vez por carga de pagina. Si el servidor sigue diciendo 401 con
      // una sesion recien hecha (por ejemplo porque firma con otra clave), la
      // recarga no arregla nada y la pantalla se queda en un bucle de entrar y
      // volver a empezar: mejor devolver el 401 y que se vea el error.
      let yaRecargado = true;
      try {
        yaRecargado = sessionStorage.getItem(RECARGA_HECHA) === '1';
        sessionStorage.setItem(RECARGA_HECHA, '1');
      } catch { /* sin almacen: no se recarga, que es lo prudente */ }
      if (!yaRecargado && typeof window !== 'undefined') window.location.reload();
      return res;
    }
    try {
      const fresh = await bootstrapSession();
      currentSession = fresh;
      onSessionRefresh?.(fresh);
      res = await doFetch(fresh.token, path, init);
    } catch {
      // refresh failed — return the original 401 so the caller can show an error
    }
  }
  return res;
}
